import genKey from 'draft-js/lib/generateRandomKey';
import sortBy from 'sort-by';

import { latexToPlainText } from './latex-utils';

export const CLEAR_TOOL_KEY = 'clear';
export const ERASER_TOOL_KEY = 'eraser';
export const LINE_THROUGH_TOOL_KEY = 'line_through';
export const OVERLAP_TOOL_KEY = 'overlap';
export const SOLUTION_TOOL_PREFIX = 'solution_';
export const HIGHLIGHT_COLOR = {
  YELLOW: 'yellow',
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  CYAN: 'cyan',
};
export const HIGHLIGHT_COLORS = Object.values(HIGHLIGHT_COLOR);

export const IMAGE_BLOCK_CLASSNAME = 'DraftEditor-imgBlock';

export function mergeContentStates(contentStates) {
  const newContentState = {
    blocks: [],
    entityMap: {},
  };
  let keyCounter = 0;
  contentStates.forEach(contentState => {
    const { blocks = [], entityMap = {} } = contentState || {};
    const entityKeyMap = Object.keys(entityMap).reduce((a, b) => {
      a[b] = keyCounter++;
      return a;
    }, {});
    blocks.forEach(b => {
      newContentState.blocks.push({
        ...b,
        key: newContentState.blocks.some(b2 => b2.key === b.key) ? genKey() : b.key || genKey(),
        entityRanges: b.entityRanges.map(er => ({ ...er, key: entityKeyMap[er.key] })), // entityRanges: [{key: 5, length: 1, offset: 0}]
      });
      b.entityRanges.forEach(er => (newContentState.entityMap[entityKeyMap[er.key]] = entityMap[er.key]));
    });
  });
  return newContentState;
}

export function insertContentState(origState, fragment, blockKey) {
  let blockIdx = origState.blocks.findIndex(b => b.key === blockKey);
  if (blockIdx === -1) blockIdx = origState.blocks.length;
  const beforeBreak = {
    ...origState,
    blocks: origState.blocks.slice(0, blockIdx),
  };
  const afterBreak = {
    ...origState,
    blocks: origState.blocks.slice(blockIdx),
  };
  return mergeContentStates([beforeBreak, fragment, afterBreak]);
}

export function extractBlocks(content, filter) {
  content ||= {};
  return mergeContentStates([
    {
      blocks: (content.blocks || []).filter(filter),
      entityMap: content.entityMap,
    },
  ]);
}

export function applyHighlightsToContent(content, highlights) {
  if (!content || !highlights) return content;
  return {
    ...content,
    blocks: content.blocks.map(b => {
      const blockHighlights = highlights[b.key];
      if (!blockHighlights?.length) return b;
      return {
        ...b,
        inlineStyleRanges: [
          ...(b.inlineStyleRanges || []),
          ...blockHighlights.map(h => ({
            offset: h.offset,
            length: h.length,
            style: h.tool === LINE_THROUGH_TOOL_KEY ? 'LINE_THROUGH' : `HIGHLIGHT_${h.tool.toUpperCase()}`,
          })),
        ],
      };
    }),
  };
}

export function hasHighlights(highlights) {
  return Object.values(highlights || {}).some(blockHighlights => blockHighlights?.length);
}

// Unions any number of highlight maps into one. Ranges are not deduped or coalesced — overlapping
// fills of the same colour render identically to a single one.
export function mergeHighlights(...highlightMaps) {
  const merged = {};
  highlightMaps.forEach(highlights => {
    Object.entries(highlights || {}).forEach(([blockKey, blockHighlights]) => {
      if (!blockHighlights?.length) return;
      merged[blockKey] = merged[blockKey] ? [...merged[blockKey], ...blockHighlights] : blockHighlights;
    });
  });
  return Object.keys(merged).length ? merged : undefined;
}

// Retags fills with the solution variant of their colour, which renders darker so authored
// highlights can be told apart from the student's own.
export function toSolutionHighlights(highlights) {
  if (!highlights) return highlights;
  return Object.fromEntries(
    Object.entries(highlights).map(([blockKey, blockHighlights]) => [
      blockKey,
      (blockHighlights || []).map(highlight =>
        highlight.tool === LINE_THROUGH_TOOL_KEY ?
          highlight
        : { ...highlight, tool: `${SOLUTION_TOOL_PREFIX}${highlight.tool}` },
      ),
    ]),
  );
}

// Ranges covered by both highlight maps, tagged with the overlap tool so they can be
// layered on top of either one. Strike-outs are not highlights, so they never overlap.
export function intersectHighlights(a, b) {
  if (!a || !b) return undefined;
  const overlaps = {};
  Object.entries(a).forEach(([blockKey, aHighlights]) => {
    const bHighlights = b[blockKey];
    if (!aHighlights?.length || !bHighlights?.length) return;

    const ranges = [];
    aHighlights.forEach(aHighlight => {
      if (aHighlight.tool === LINE_THROUGH_TOOL_KEY) return;
      bHighlights.forEach(bHighlight => {
        if (bHighlight.tool === LINE_THROUGH_TOOL_KEY) return;
        const offset = Math.max(aHighlight.offset, bHighlight.offset);
        const length = Math.min(aHighlight.offset + aHighlight.length, bHighlight.offset + bHighlight.length) - offset;
        if (length > 0) ranges.push({ offset, length, tool: OVERLAP_TOOL_KEY });
      });
    });
    if (ranges.length) overlaps[blockKey] = ranges;
  });
  return overlaps;
}

export function hasContent(content) {
  return (
    content &&
    content.blocks &&
    content.blocks.some(b => b.type === 'atomic' || !!b.text.trim() || (b.entityRanges && b.entityRanges.length))
  );
}
// Draft content nested inside an entity's data: a callout body, or every cell of a table.
export function getNestedContents(entity) {
  if (entity?.data?.content) return [entity.data.content];
  if (entity?.data?.rows) {
    return entity.data.rows.flatMap(row => (row.cells || []).map(cell => cell.content)).filter(Boolean);
  }
  return [];
}

export function hasSolution(content) {
  if (!content) return false;
  return !!(
    content.blocks?.some(block => block.data?.solution) ||
    (content.entityMap &&
      Object.values(content.entityMap).some(
        e => e.type === 'FILL_IN_THE_BLANK' || getNestedContents(e).some(hasSolution),
      ))
  );
}

// Which DOM node and offset a viewport point falls on, or null where there is no caret position to
// be had. `caretPositionFromPoint` is the standardised one (Firefox, and Chrome from 128); Safari
// and older Chrome only implement the original `caretRangeFromPoint`.
export function getCaretPositionFromPoint(x, y) {
  if (document.caretPositionFromPoint) {
    const position = document.caretPositionFromPoint(x, y);
    return position?.offsetNode && { node: position.offsetNode, offset: position.offset };
  }
  if (document.caretRangeFromPoint) {
    const range = document.caretRangeFromPoint(x, y);
    return range?.startContainer && { node: range.startContainer, offset: range.startOffset };
  }
}

export function textToDraftJs(text) {
  // const contentState = ContentState.createFromText(text);
  // return convertToRaw(contentState);
  return {
    entityMap: {},
    blocks: (text || '').split(/\r\n?|\n/).map(text => ({
      key: genKey(),
      text: text || ' ',
      type: 'unstyled',
      entityRanges: [],
      inlineStyleRanges: [],
    })),
  };
}

// Cells are flattened onto a single line each so the tab/newline grid survives multi-block cells.
function tableToText(data) {
  return (data.rows || [])
    .map(row => (row.cells || []).map(cell => draftJsToText(cell.content).replace(/\s+/g, ' ').trim()).join('\t'))
    .join('\n');
}

export function draftJsToText(content) {
  if (!content) return '';
  return content?.blocks
    ?.map(b => {
      if (b.type === 'atomic') {
        const entity = content.entityMap[b.entityRanges[0]?.key];
        if (entity?.type === 'EQUATION') return latexToPlainText(entity.data.latex);
        if (entity?.type === 'TABLE') return tableToText(entity.data);
      } else {
        const text = b.text;
        const ranges = b.entityRanges;
        if (ranges?.length) {
          let str = '';
          let curPos = 0;
          [...ranges].sort(sortBy('offset')).forEach(er => {
            const offset = er.offset;
            const length = er.length;
            if (offset > curPos) str += text.substring(curPos, offset);
            const entity = content.entityMap[er.key];
            switch (entity.type) {
              case 'EQUATION':
                str += latexToPlainText(entity.data.latex);
                break;
              case 'LINK':
              case 'CITE': // Essay Editor
                str += text.substring(offset, offset + length);
                break;
            }
            curPos = offset + length;
          });
          str += text.substring(curPos);
          return str;
        } else {
          return text;
        }
      }
    })
    .filter(Boolean)
    .join('\n');
}
